import React, { useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Button from '@amzn/meridian/button';
import DataTable, {
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableHeader,
} from '@amzn/meridian/data-table';
import { useAppSelector } from '../../../store';
import { ProcessPathFunctionType } from '../model';

interface ConfirmPlanGenerationProps {
  onGenerate: () => void;
  onGoBack: () => void;
  loading?: boolean;
}

const ConfirmPlanGeneration: React.FC<ConfirmPlanGenerationProps> = ({ onGenerate, onGoBack, loading = false }) => {
  const dryRunResponse = useAppSelector((state) => state.checkout.dryRunResponse);
  const appliedDryRunResponse = useAppSelector((state) => state.checkout.appliedDryRunResponse);
  const outputAdjustments = useAppSelector((state) => state.checkout.outputAdjustments);

  // Compute summary stats
  const summaryStats = useMemo(() => {
    if (!dryRunResponse || !appliedDryRunResponse) {
      return { originalTotalHC: 0, newTotalHC: 0, netChange: 0, totalHours: 0 };
    }

    const originalTotalHC = dryRunResponse.availableHeadcount;
    const newTotalHC = appliedDryRunResponse.tunedHeadcount
      ?? appliedDryRunResponse.processPath.reduce((sum, entry) => sum + entry.headcount, 0);

    // Compute net change from per-path deltas (applied HC - original HC)
    const netChange = appliedDryRunResponse.processPath.reduce((sum, entry) => {
      const originalEntry = dryRunResponse.processPath.find((pp) => pp.functionName === entry.functionName);
      const originalHC = originalEntry?.headcount ?? 0;
      return sum + (entry.headcount - originalHC);
    }, 0);

    // Also account for paths that were excluded (present in baseline but not in applied)
    const excludedDelta = dryRunResponse.processPath.reduce((sum, baseEntry) => {
      const inApplied = appliedDryRunResponse.processPath.find((pp) => pp.functionName === baseEntry.functionName);
      if (!inApplied) {
        return sum - baseEntry.headcount;
      }
      return sum;
    }, 0);

    const totalHours = appliedDryRunResponse.totalHours;

    return { originalTotalHC, newTotalHC, netChange: netChange + excludedDelta, totalHours };
  }, [dryRunResponse, appliedDryRunResponse]);

  // Sort applied process paths: DIRECT first, then by headcount desc
  const sortedPaths = useMemo(() => {
    if (!appliedDryRunResponse?.processPath) return [];
    return [...appliedDryRunResponse.processPath].sort((a, b) => {
      if (a.type === b.type) return b.headcount - a.headcount;
      return a.type === ProcessPathFunctionType.DIRECT ? -1 : 1;
    });
  }, [appliedDryRunResponse]);

  // Get original headcount for a path from baseline response
  const getOriginalHC = (functionName: string): number => {
    const entry = dryRunResponse?.processPath.find((pp) => pp.functionName === functionName);
    return entry?.headcount ?? 0;
  };

  // Get override reason for a path
  const getOverrideReason = (functionName: string): string => {
    const adj = outputAdjustments.find((a) => a.functionName === functionName);
    return adj?.overrideReason || '—';
  };

  if (!appliedDryRunResponse) return null;

  return (
    <Column spacing="400" spacingInset="300">
      <Heading level={2} type="h400">Confirm Plan Generation</Heading>
      <Text type="b200" color="secondary">
        Review changes below before generating the plan. This will update the shift plan output.
      </Text>

      <Box type="fill" spacingInset="300">
        <Row spacing="500" wrap="down">
          <Column>
            <Text type="b100" color="secondary">ORIGINAL TOTAL HC</Text>
            <Text type="d100">{summaryStats.originalTotalHC}</Text>
          </Column>
          <Column>
            <Text type="b100" color="secondary">NEW TOTAL HC</Text>
            <Text type="d100">{summaryStats.newTotalHC}</Text>
          </Column>
          <Column>
            <Text type="b100" color="secondary">NET CHANGE</Text>
            <Text type="d100" color={summaryStats.netChange > 0 ? 'success' : summaryStats.netChange < 0 ? 'error' : undefined}>
              {summaryStats.netChange >= 0 ? '+' : ''}{summaryStats.netChange}
            </Text>
          </Column>
          <Column>
            <Text type="b100" color="secondary">TOTAL HOURS</Text>
            <Text type="d100">{summaryStats.totalHours.toFixed(2)}</Text>
          </Column>
        </Row>
      </Box>

      <Box overflowX="auto">
        <DataTable showRowDividers width="100%">
          <DataTableHead backgroundColor="secondary">
            <DataTableRow>
              <DataTableHeader>Process Path</DataTableHeader>
              <DataTableHeader>Original HC</DataTableHeader>
              <DataTableHeader>New HC</DataTableHeader>
              <DataTableHeader>Delta</DataTableHeader>
              <DataTableHeader>Hours</DataTableHeader>
              <DataTableHeader>Hrs / 1000 units</DataTableHeader>
              <DataTableHeader>Note</DataTableHeader>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {sortedPaths.map((entry) => {
              const originalHC = getOriginalHC(entry.functionName);
              const delta = entry.headcount - originalHC;

              const deltaClass = (() => {
                if (delta === 0) return 'delta--neutral';
                const absDelta = Math.abs(delta);
                const pctChange = originalHC > 0 ? (absDelta / originalHC) * 100 : 0;
                if (absDelta <= 1 || pctChange <= 5) return 'delta--warning';
                return 'delta--critical';
              })();

              const isModified = delta !== 0;
              const cellBg = isModified ? 'error' : undefined;

              return (
                <DataTableRow key={entry.functionName} highlightOnHover>
                  <DataTableCell backgroundColor={cellBg}>
                    <Text type="b300">{entry.functionName}</Text>
                  </DataTableCell>
                  <DataTableCell backgroundColor={cellBg}>
                    <Text type="b300">{originalHC}</Text>
                  </DataTableCell>
                  <DataTableCell backgroundColor={cellBg}>
                    <Text type="b300">{entry.headcount}</Text>
                  </DataTableCell>
                  <DataTableCell backgroundColor={cellBg}>
                    <Text type="b300" className={deltaClass}>
                      {delta >= 0 ? '+' : ''}{delta}
                    </Text>
                  </DataTableCell>
                  <DataTableCell backgroundColor={cellBg}>
                    <Text type="b300">{entry.hours != null ? entry.hours.toFixed(2) : '—'}</Text>
                  </DataTableCell>
                  <DataTableCell backgroundColor={cellBg}>
                    <Text type="b300">{entry.hoursPer1000Units != null ? entry.hoursPer1000Units.toFixed(2) : '—'}</Text>
                  </DataTableCell>
                  <DataTableCell backgroundColor={cellBg}>
                    <Text type="b200" color="secondary">{getOverrideReason(entry.functionName)}</Text>
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </DataTableBody>
        </DataTable>
      </Box>

      {/* Excluded paths section */}
      {(() => {
        const excludedAdjs = outputAdjustments.filter((adj) => adj.disabled);

        if (excludedAdjs.length === 0) return null;

        return (
          <Column spacing="200">
            <Text type="b300">Excluded Paths ({excludedAdjs.length})</Text>
            <Box type="fill" spacingInset="300">
              <Column spacing="200">
                {excludedAdjs.map((adj) => {
                  const baselineEntry = dryRunResponse?.processPath.find((pp) => pp.functionName === adj.functionName);
                  const originalHC = baselineEntry?.headcount ?? 0;
                  const originalHours = baselineEntry?.hours ?? 0;

                  return (
                    <Row key={adj.functionName} spacing="200">
                      <Text type="b200" color="secondary">
                        <span className="process-path-name--excluded">{adj.functionName}</span>
                      </Text>
                      <Text type="b200" color="secondary">
                        (was {originalHC} HC, {originalHours} Hours)
                      </Text>
                    </Row>
                  );
                })}
              </Column>
            </Box>
          </Column>
        );
      })()}

      <Row spacing="300">
        <Button type="primary" onClick={onGenerate} disabled={loading}>
          {loading ? 'Generating...' : 'Generate Plan'}
        </Button>
        <Button type="tertiary" onClick={onGoBack} disabled={loading}>
          Go Back
        </Button>
      </Row>
    </Column>
  );
};

export default ConfirmPlanGeneration;
